import { z } from 'zod';
/**
 * The scope grammar (Constitution §6).
 *
 * A scope is `<department>.<capability>`, where capability may be `*`.
 * Deliberately shallow: a grammar with nesting, negation or wildcards in the
 * middle is one where "does this grant that?" stops being obvious by reading
 * it, and an authorization rule nobody can read by eye is one nobody can
 * audit.
 *
 *   outbound.*          every capability in outbound
 *   outbound.read       one capability
 *
 * There is no `*` on its own and no `*.read`. A grant is always anchored to a
 * named department, so "everything everywhere" is not expressible.
 *
 * A department agent holds scopes in its own department only, and
 * `effectiveScopes` strips anything else on every call (§6). This comment
 * once listed `finance.read` as "another department's read capability, if
 * granted"; it never was, and ADR-0039 removed the claim. Work that needs
 * another department goes through a request (§18), which confers nothing.
 */
export const SCOPE_PATTERN = /^[a-z][a-z_]*\.(\*|[a-z][a-z_]*)$/;
export const scopeSchema = z
    .string()
    .regex(SCOPE_PATTERN, 'a scope is <department>.<capability>, e.g. outbound.read or outbound.*');
export function parseScope(scope) {
    const match = SCOPE_PATTERN.exec(scope);
    if (!match)
        throw new Error(`malformed scope: ${scope}`);
    const [department, capability] = scope.split('.');
    return { department, capability, wildcard: capability === '*' };
}
/**
 * Does `granted` permit `required`?
 *
 * Wildcards widen only within their own department: `outbound.*` never
 * reaches `finance.anything`. That is §6's per-department isolation, enforced
 * by the grammar rather than by remembering to check it.
 */
export function scopePermits(granted, required) {
    const g = parseScope(granted);
    const r = parseScope(required);
    if (g.department !== r.department)
        return false;
    if (g.wildcard)
        return true;
    // A holder of a specific capability does not get the wildcard.
    return g.capability === r.capability;
}
/**
 * Does a department's `never:` list forbid this scope or tool id?
 *
 * A floor is written in scope grammar with one addition the grant grammar
 * refuses: a wildcard department, `*.admin`, which ADR-0124 puts on every
 * list. `anyScopePermits` rightly throws on that, since no grant may say
 * "every department" — and so a floor check built on it threw instead of
 * deciding, for every department whose list carries the floor. This reads
 * `*.<capability>` as that capability in any department, `<dept>.*` as
 * everything in that department, and anything malformed as forbidding
 * nothing it does not literally name.
 */
export function neverForbids(never, candidate) {
    const [cd, cc] = candidate.split('.');
    if (!cd || !cc)
        return false;
    return never.some((entry) => {
        const [nd, nc] = entry.split('.');
        if (!nd || !nc)
            return false;
        return (nd === '*' || nd === cd) && (nc === '*' || nc === cc);
    });
}
/** Does any scope in the set permit the required one? */
export function anyScopePermits(granted, required) {
    return granted.some((g) => scopePermits(g, required));
}
/**
 * Intersect two scope sets: the strongest grant present in BOTH.
 *
 * Used for the digital-twin ceiling (§16), where a twin's effective
 * permission is its own grants intersected with its principal's. The
 * intersection of `outbound.*` and `outbound.read` is `outbound.read` — the
 * narrower of the two — because a twin must never exceed its principal.
 */
export function intersectScopes(a, b) {
    const result = new Set();
    for (const left of a) {
        for (const right of b) {
            const l = parseScope(left);
            const r = parseScope(right);
            if (l.department !== r.department)
                continue;
            if (l.wildcard && r.wildcard)
                result.add(left);
            else if (l.wildcard)
                result.add(right); // b is narrower
            else if (r.wildcard)
                result.add(left); // a is narrower
            else if (l.capability === r.capability)
                result.add(left);
        }
    }
    return [...result].sort();
}
//# sourceMappingURL=scopes.js.map